/**
 * LeagueVenuesPage — /league/:leagueId/venues
 * ─────────────────────────────────────────────────────────────────
 * Manage the venues a league plays at: their name + address, the
 * weekly slots when they're bookable (Mondays 6-10pm, Tuesdays
 * 7-9pm, etc.), and blackout dates when they're not available
 * despite a normal slot (holidays, tournaments, ice cut for repairs).
 *
 * Permission gates (mirroring backend exactly):
 *   • Any member can view venues.
 *   • Creating/editing/deleting venues + slots + blackouts requires
 *     manage_venues (managers bypass).
 *
 * UI structure:
 *   • Page header + "Add venue" button
 *   • Venues list (cards with name, address, counts)
 *   • Clicking a card opens a detail panel showing:
 *       - Venue metadata edit form
 *       - Weekly slots list (with inline add/edit/delete)
 *       - Blackouts list (with add date picker)
 *
 * For Phase 2b the detail is rendered as an expandable inline
 * section per venue rather than a separate page — keeps the routing
 * simple and lets a manager configure multiple venues without
 * page-hopping. Phase 4 (schedule generator) will need a richer
 * visualization (weekly grid) but Phase 2b is CRUD-first.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  MapPin, Plus, X, Save, Trash2, Calendar, Clock, AlertTriangle,
  ChevronDown, ChevronRight, Edit2, Upload,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
// v2.5.126 — Phase 11.2. Address inputs use the Maps-backed
// autocomplete when configured; otherwise fall back to plain text.
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function LeagueVenuesPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canManage = isManager || can('manage_venues');

  const [league, setLeague] = useState(null);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);  // venue id of currently-open detail
  const [showCreate, setShowCreate] = useState(false);

  async function loadAll() {
    if (!leagueId) return;
    try {
      const [leagueRes, venuesRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/venues`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setVenues(venuesRes?.data?.venues || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load venues');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [leagueId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 980 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 980 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 980 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Venues</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Where the league plays. Add weekly slots + blackout dates for the schedule generator to use.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn-ice"
            onClick={() => setShowCreate(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <Plus size={14} />
            Add venue
          </button>
        )}
      </div>

      {!canManage && (
        <div className="card" style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-2)' }}>
          You can see the venues but can't make changes. The <code>manage_venues</code> permission
          is required.
        </div>
      )}

      {venues.length === 0 ? (
        <EmptyVenuesCard canManage={canManage} onCreate={() => setShowCreate(true)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {venues.map((v) => (
            <VenueCard
              key={v.id}
              venue={v}
              leagueId={leagueId}
              canManage={canManage}
              expanded={expandedId === v.id}
              onToggle={() => setExpandedId(expandedId === v.id ? null : v.id)}
              onUpdated={loadAll}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateVenueModal
          leagueId={leagueId}
          onClose={() => setShowCreate(false)}
          onCreated={async (newVenue) => {
            setShowCreate(false);
            await loadAll();
            setExpandedId(newVenue?.id || null);  // auto-expand the new venue
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Empty state
// ───────────────────────────────────────────────────────────────

function EmptyVenuesCard({ canManage, onCreate }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
      <MapPin size={40} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No venues yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 420, margin: '0 auto 18px' }}>
        Add the rinks, parks, or fields where this league plays. Each venue can have weekly
        recurring slots and blackout dates.
      </div>
      {canManage && (
        <button type="button" className="btn-ice" onClick={onCreate}>
          <Plus size={14} style={{ marginRight: 8 }} />
          Add your first venue
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Venue card (collapsible — header + expandable detail)
// ───────────────────────────────────────────────────────────────

function VenueCard({ venue, leagueId, canManage, expanded, onToggle, onUpdated }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '14px 18px',
          background: 'transparent', border: 'none',
          color: 'var(--text)', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'var(--ice-a10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <MapPin size={16} color="var(--ice)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>
            {venue.name}
            {!venue.is_active && (
              <span className="badge" style={{
                marginLeft: 8, background: 'var(--surface-2)', color: 'var(--text-2)',
                fontSize: 10, padding: '2px 8px',
              }}>
                Inactive
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
            {venue.address || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>No address</span>}
            <span style={{ margin: '0 6px' }}>·</span>
            {venue.slot_count} weekly slot{venue.slot_count === 1 ? '' : 's'}
            <span style={{ margin: '0 6px' }}>·</span>
            {venue.blackout_count} blackout{venue.blackout_count === 1 ? '' : 's'}
          </div>
        </div>
        {expanded ? <ChevronDown size={18} color="var(--text-2)" /> : <ChevronRight size={18} color="var(--text-2)" />}
      </button>

      {expanded && (
        <VenueDetail venue={venue} leagueId={leagueId} canManage={canManage} onUpdated={onUpdated} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Venue detail (expanded section: metadata edit + slots + blackouts)
// ───────────────────────────────────────────────────────────────

function VenueDetail({ venue, leagueId, canManage, onUpdated }) {
  // Local state for the full venue detail. We fetch once on expand
  // and edit in place; saves trigger a parent reload.
  const [slots, setSlots] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({
    name: venue.name,
    address: venue.address || '',
    notes: venue.notes || '',
    is_active: venue.is_active,
  });
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/leagues/${leagueId}/venues/${venue.id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setSlots(data?.slots || []);
        setBlackouts(data?.blackouts || []);
      })
      .catch(() => { if (!cancelled) toast.error('Could not load venue details'); })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [venue.id]);

  async function saveMeta() {
    setSavingMeta(true);
    try {
      await api.patch(`/leagues/${leagueId}/venues/${venue.id}`, {
        name: metaForm.name.trim(),
        address: metaForm.address.trim() || null,
        notes: metaForm.notes.trim() || null,
        is_active: metaForm.is_active,
      });
      toast.success('Venue updated');
      setEditingMeta(false);
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally {
      setSavingMeta(false);
    }
  }

  async function deleteVenue() {
    const ok = window.confirm(
      `Deactivate "${venue.name}"? Games already scheduled here are unaffected. ` +
      'The venue will be hidden from active lists.'
    );
    if (!ok) return;
    try {
      await api.delete(`/leagues/${leagueId}/venues/${venue.id}`);
      toast.success('Venue deactivated');
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not deactivate');
    }
  }

  async function reloadDetail() {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/venues/${venue.id}`);
      setSlots(data?.slots || []);
      setBlackouts(data?.blackouts || []);
      onUpdated();  // also refresh parent counts
    } catch (_e) { /* toast handled by callers */ }
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px', background: 'var(--surface-2)' }}>
      {/* Metadata block */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Venue info
          </div>
          {canManage && !editingMeta && (
            <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => setEditingMeta(true)}>
              <Edit2 size={11} style={{ marginRight: 4 }} />
              Edit
            </button>
          )}
        </div>

        {editingMeta ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input type="text" value={metaForm.name} maxLength={100}
              onChange={(e) => setMetaForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Venue name" style={inputStyle} />
            <AddressAutocomplete value={metaForm.address}
              onChange={(v) => setMetaForm(f => ({ ...f, address: v }))}
              placeholder="Address" style={inputStyle} />
            <textarea value={metaForm.notes}
              onChange={(e) => setMetaForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Notes (optional)" rows={2}
              style={{ ...inputStyle, resize: 'vertical' }} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={metaForm.is_active}
                onChange={(e) => setMetaForm(f => ({ ...f, is_active: e.target.checked }))} />
              Active (uncheck to hide from active lists)
            </label>
            {/* Mobile fix: allow the Deactivate + Cancel/Save cluster to
                wrap instead of clipping at ~390px widths. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="btn-ghost"
                onClick={deleteVenue} disabled={savingMeta}
                style={{ color: 'var(--penalty)', borderColor: 'var(--penalty-a30)', fontSize: 12, padding: '6px 12px' }}>
                <Trash2 size={12} style={{ marginRight: 4 }} />
                Deactivate
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-ghost" style={tinyBtn}
                  onClick={() => {
                    setEditingMeta(false);
                    setMetaForm({ name: venue.name, address: venue.address || '', notes: venue.notes || '', is_active: venue.is_active });
                  }} disabled={savingMeta}>
                  Cancel
                </button>
                <button type="button" className="btn-ice" style={tinyBtn}
                  onClick={saveMeta} disabled={savingMeta || !metaForm.name.trim()}>
                  <Save size={11} style={{ marginRight: 4 }} />
                  {savingMeta ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            {venue.notes && <div style={{ marginBottom: 6 }}>{venue.notes}</div>}
            {!venue.notes && <span style={{ fontStyle: 'italic', opacity: 0.6 }}>No notes.</span>}
          </div>
        )}
      </div>

      {/* Slots block */}
      <SlotsSection
        leagueId={leagueId}
        venueId={venue.id}
        venueName={venue.name}
        slots={slots}
        canManage={canManage}
        loadingDetail={loadingDetail}
        onChange={reloadDetail}
      />

      {/* Blackouts block */}
      <BlackoutsSection
        leagueId={leagueId}
        venueId={venue.id}
        blackouts={blackouts}
        canManage={canManage}
        loadingDetail={loadingDetail}
        onChange={reloadDetail}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Slots section
// ───────────────────────────────────────────────────────────────

function SlotsSection({ leagueId, venueId, venueName, slots, canManage, loadingDetail, onChange }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptySlotForm());
  // v2.5.126 (Phase 12) — import-slots modal state.
  const [importOpen, setImportOpen] = useState(false);

  function emptySlotForm() {
    return {
      day_of_week: 1,
      start_time: '18:00',
      duration_minutes: 60,
      notes: '',
      recurrence_pattern: 'weekly',
      recurrence_anchor: '',
      recurrence_nth_week: 1,
      recurrence_day_of_month: 1,
      effective_from: '',
      effective_to: '',
      recurrence_count: '',
    };
  }

  function startAdd() {
    setForm(emptySlotForm());
    setAdding(true);
    setEditingId(null);
  }
  function startEdit(slot) {
    setForm({
      day_of_week: slot.day_of_week,
      start_time: (slot.start_time || '').slice(0, 5),
      duration_minutes: slot.duration_minutes,
      notes: slot.notes || '',
      recurrence_pattern: slot.recurrence_pattern || 'weekly',
      recurrence_anchor: slot.recurrence_anchor || '',
      recurrence_nth_week: slot.recurrence_nth_week || 1,
      recurrence_day_of_month: slot.recurrence_day_of_month || 1,
      effective_from: slot.effective_from || '',
      effective_to: slot.effective_to || '',
      recurrence_count: slot.recurrence_count != null ? String(slot.recurrence_count) : '',
    });
    setEditingId(slot.id);
    setAdding(false);
  }
  function cancel() {
    setAdding(false);
    setEditingId(null);
  }

  // v2.5.126 — build the API payload from form state. Only sends
  // recurrence-supporting fields when the pattern actually uses
  // them, so the DB CHECK constraint stays happy.
  function buildPayload() {
    const payload = {
      day_of_week: form.day_of_week,
      start_time: form.start_time,
      duration_minutes: form.duration_minutes,
      notes: form.notes.trim() || null,
      recurrence_pattern: form.recurrence_pattern || 'weekly',
      effective_from: form.effective_from || null,
      effective_to: form.effective_to || null,
    };
    if (form.recurrence_pattern === 'biweekly') {
      payload.recurrence_anchor = form.recurrence_anchor || null;
    } else {
      payload.recurrence_anchor = null;
    }
    if (form.recurrence_pattern === 'monthly_nth_dow') {
      payload.recurrence_nth_week = Number(form.recurrence_nth_week) || 1;
    } else {
      payload.recurrence_nth_week = null;
    }
    if (form.recurrence_pattern === 'monthly_date') {
      payload.recurrence_day_of_month = Number(form.recurrence_day_of_month) || 1;
    } else {
      payload.recurrence_day_of_month = null;
    }
    const n = Number(form.recurrence_count);
    payload.recurrence_count = (Number.isInteger(n) && n > 0) ? n : null;
    return payload;
  }

  async function save() {
    const payload = buildPayload();
    try {
      if (editingId) {
        await api.patch(`/leagues/${leagueId}/venues/${venueId}/slots/${editingId}`, payload);
        toast.success('Slot updated');
      } else {
        await api.post(`/leagues/${leagueId}/venues/${venueId}/slots`, payload);
        toast.success('Slot added');
      }
      cancel();
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save slot');
    }
  }

  async function remove(slot) {
    const ok = window.confirm(`Delete this slot (${DAY_NAMES[slot.day_of_week]} ${formatTime(slot.start_time)})?`);
    if (!ok) return;
    try {
      await api.delete(`/leagues/${leagueId}/venues/${venueId}/slots/${slot.id}`);
      toast.success('Slot deleted');
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete slot');
    }
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={14} color="var(--ice)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Weekly slots
          </span>
        </div>
        {canManage && !adding && !editingId && (
          // Mobile fix: wrap so "Import from file" + "Add slot" don't
          // clip against the title on the left at ~390px widths.
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* v2.5.126 — AI-driven bulk import. Lives next to "Add
                slot" so managers find it in the obvious place. */}
            <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => setImportOpen(true)}>
              <Upload size={11} style={{ marginRight: 4 }} />
              Import from file
            </button>
            <button type="button" className="btn-ghost" style={tinyBtn} onClick={startAdd}>
              <Plus size={11} style={{ marginRight: 4 }} />
              Add slot
            </button>
          </div>
        )}
      </div>

      {loadingDetail ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      ) : slots.length === 0 && !adding ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic', padding: '8px 0' }}>
          No weekly slots yet. Add one so the schedule generator knows when this venue is bookable.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {slots.map((slot) => (
            editingId === slot.id ? (
              <SlotEditForm key={slot.id} form={form} setForm={setForm} onCancel={cancel} onSave={save} />
            ) : (
              <SlotRow key={slot.id} slot={slot} canManage={canManage} onEdit={() => startEdit(slot)} onDelete={() => remove(slot)} />
            )
          ))}
          {adding && (
            <SlotEditForm form={form} setForm={setForm} onCancel={cancel} onSave={save} />
          )}
        </div>
      )}

      {importOpen && (
        <SlotImportModal
          leagueId={leagueId}
          venueId={venueId}
          venueName={venueName}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); onChange(); }}
        />
      )}
    </div>
  );
}

function SlotRow({ slot, canManage, onEdit, onDelete }) {
  // v2.5.126 — humanize the recurrence pattern for display. Default
  // 'weekly' stays unlabeled (it's the implicit default for the
  // existing UX). Non-default patterns get a small label so the
  // manager can see at a glance which slots are biweekly or monthly.
  const pattern = slot.recurrence_pattern || 'weekly';
  let patternLabel = '';
  if (pattern === 'biweekly') {
    patternLabel = 'Biweekly';
    if (slot.recurrence_anchor) patternLabel += ` (from ${slot.recurrence_anchor})`;
  } else if (pattern === 'monthly_nth_dow') {
    const nth = slot.recurrence_nth_week;
    const nthLabel = nth === 5 ? 'Last' : nth === 1 ? '1st' : nth === 2 ? '2nd' : nth === 3 ? '3rd' : `${nth}th`;
    patternLabel = `${nthLabel} ${DAY_NAMES[slot.day_of_week]} of each month`;
  } else if (pattern === 'monthly_date') {
    patternLabel = `${slot.recurrence_day_of_month} of each month`;
  }
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '8px 12px',
      background: 'var(--surface)', borderRadius: 8,
      border: '1px solid var(--border)',
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 8,
        background: 'var(--surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: 'var(--text)',
        flexShrink: 0,
      }}>
        {DAY_NAMES[slot.day_of_week]}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {formatTime(slot.start_time)} · {slot.duration_minutes} min
          {patternLabel && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700,
              color: 'var(--gold)', background: 'var(--gold-a15)',
              padding: '1px 6px', borderRadius: 999,
              letterSpacing: '0.05em',
            }}>
              {patternLabel}
            </span>
          )}
          {slot.slot_import_id && (
            <span style={{
              marginLeft: 6, fontSize: 9, fontWeight: 700,
              color: 'var(--text-3)',
              padding: '1px 6px', borderRadius: 999,
              border: '1px solid var(--border)',
            }} title={`Imported via AI (import ${slot.slot_import_id.slice(0, 8)}…)`}>
              IMPORTED
            </span>
          )}
        </div>
        {slot.notes && (
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{slot.notes}</div>
        )}
        {(slot.effective_from || slot.effective_to || slot.recurrence_count) && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
            {slot.effective_from || '—'} to {slot.effective_to || 'open-ended'}
            {slot.recurrence_count ? ` · ${slot.recurrence_count} occurrence${slot.recurrence_count === 1 ? '' : 's'}` : ''}
          </div>
        )}
      </div>
      {canManage && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={onEdit}>Edit</button>
          <button type="button" className="btn-ghost" style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={onDelete}>
            <Trash2 size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

function SlotEditForm({ form, setForm, onCancel, onSave }) {
  const pattern = form.recurrence_pattern || 'weekly';
  return (
    <div style={{
      padding: 12, borderRadius: 8,
      background: 'var(--surface)', border: '1px solid var(--ice-a25)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Day + time + duration. These don't change semantics across
          patterns — they answer "WHEN on the chosen day/date does
          this slot fall?" */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={form.day_of_week}
          onChange={(e) => setForm(f => ({ ...f, day_of_week: parseInt(e.target.value, 10) }))}
          style={{ ...inputStyle, width: 'auto', minWidth: 100 }}>
          {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
        </select>
        <input type="time" value={form.start_time}
          onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
          style={{ ...inputStyle, width: 'auto', minWidth: 110 }} />
        <input type="number" value={form.duration_minutes} min={1} max={1440}
          onChange={(e) => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value, 10) || 0 }))}
          style={{ ...inputStyle, width: 80 }} />
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>min</span>
      </div>

      {/* v2.5.126 — recurrence pattern picker. Default 'weekly' so
          existing UX is unchanged. Other patterns unlock additional
          fields below. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 600, letterSpacing: 0.5 }}>
          REPEATS
        </span>
        <select value={pattern}
          onChange={(e) => setForm(f => ({ ...f, recurrence_pattern: e.target.value }))}
          style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
          <option value="weekly">Every week</option>
          <option value="biweekly">Every other week</option>
          <option value="monthly_nth_dow">Nth weekday of the month</option>
          <option value="monthly_date">Specific day of the month</option>
        </select>
        {pattern === 'biweekly' && (
          <>
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>starting</span>
            <input type="date" value={form.recurrence_anchor}
              onChange={(e) => setForm(f => ({ ...f, recurrence_anchor: e.target.value }))}
              style={{ ...inputStyle, width: 'auto' }} />
          </>
        )}
        {pattern === 'monthly_nth_dow' && (
          <>
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>which week:</span>
            <select value={form.recurrence_nth_week}
              onChange={(e) => setForm(f => ({ ...f, recurrence_nth_week: parseInt(e.target.value, 10) }))}
              style={{ ...inputStyle, width: 'auto' }}>
              <option value={1}>1st</option>
              <option value={2}>2nd</option>
              <option value={3}>3rd</option>
              <option value={4}>4th</option>
              <option value={5}>Last</option>
            </select>
          </>
        )}
        {pattern === 'monthly_date' && (
          <>
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>day of month:</span>
            <input type="number" min={1} max={31} value={form.recurrence_day_of_month}
              onChange={(e) => setForm(f => ({ ...f, recurrence_day_of_month: parseInt(e.target.value, 10) || 1 }))}
              style={{ ...inputStyle, width: 70 }} />
          </>
        )}
      </div>

      {/* Date range + occurrence cap — applicable to every pattern.
          Both optional; null means open-ended on that side. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 600, letterSpacing: 0.5 }}>
          DATES
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-2)' }}>from</span>
        <input type="date" value={form.effective_from}
          onChange={(e) => setForm(f => ({ ...f, effective_from: e.target.value }))}
          style={{ ...inputStyle, width: 'auto' }} />
        <span style={{ fontSize: 11, color: 'var(--text-2)' }}>to</span>
        <input type="date" value={form.effective_to}
          onChange={(e) => setForm(f => ({ ...f, effective_to: e.target.value }))}
          style={{ ...inputStyle, width: 'auto' }} />
        <span style={{ fontSize: 11, color: 'var(--text-2)' }}>or end after</span>
        <input type="number" min={1} max={520} value={form.recurrence_count}
          onChange={(e) => setForm(f => ({ ...f, recurrence_count: e.target.value }))}
          placeholder="N"
          style={{ ...inputStyle, width: 70 }} />
        <span style={{ fontSize: 11, color: 'var(--text-2)' }}>occurrences</span>
      </div>

      <input type="text" value={form.notes} maxLength={200}
        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
        placeholder="Notes (optional)" style={inputStyle} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn-ghost" style={tinyBtn} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-ice" style={tinyBtn} onClick={onSave}>
          <Save size={11} style={{ marginRight: 4 }} />
          Save
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// SlotImportModal (v2.5.126, Phase 12)
// ───────────────────────────────────────────────────────────────
//
// Two-step flow: upload → preview (with sanity-check banner if
// the AI-detected venue name doesn't match the one the manager
// picked) → curated confirm. Manager can deselect rows and edit
// any field inline before confirming.

function SlotImportModal({ leagueId, venueId, venueName, onClose, onImported }) {
  const [phase, setPhase] = useState('pick'); // 'pick' | 'uploading' | 'preview' | 'saving'
  const [file, setFile] = useState(null);
  const [importId, setImportId] = useState(null);
  const [extracted, setExtracted] = useState(null); // { slots, venue_name_detected, ... }
  const [rows, setRows]           = useState([]);   // editable preview rows
  const [otherVenues, setOtherVenues] = useState([]); // for reassign dropdown
  const [reassignTo,  setReassignTo]  = useState('');

  // Load the league's other venues once we hit preview so the
  // manager can "reassign" if they uploaded to the wrong venue.
  useEffect(() => {
    if (phase !== 'preview') return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}/venues`)
      .then(r => {
        if (cancelled) return;
        const others = (r.data?.venues || []).filter(v => v.id !== venueId);
        setOtherVenues(others);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [phase, leagueId, venueId]);

  async function startExtract() {
    if (!file) return;
    setPhase('uploading');
    try {
      // Read the file as base64 for the API. Same pattern as the
      // team-side schedule importer.
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result || '').toString().split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post(
        `/leagues/${leagueId}/venues/${venueId}/slots/import/extract`,
        { filename: file.name, mimeType: file.type, data: base64, fileSize: file.size }
      );
      setImportId(data.import_id);
      setExtracted(data);
      // Each row gets an `include` flag the manager can toggle.
      setRows((data.slots || []).map(s => ({ ...s, include: true })));
      setPhase('preview');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not extract slots from this file');
      setPhase('pick');
    }
  }

  async function cancelImport() {
    if (importId) {
      try {
        await api.post(`/leagues/${leagueId}/venues/${venueId}/slots/import/${importId}/cancel`);
      } catch { /* non-fatal */ }
    }
    onClose();
  }

  async function confirmImport() {
    const slots = rows.filter(r => r.include).map(r => {
      // Strip the UI-only `include` flag and any null recurrence
      // fields that don't apply to the chosen pattern.
      const out = {
        day_of_week: r.day_of_week,
        start_time: r.start_time,
        duration_minutes: r.duration_minutes,
        notes: r.notes || null,
        recurrence_pattern: r.recurrence_pattern || 'weekly',
      };
      if (r.recurrence_pattern === 'biweekly')         out.recurrence_anchor = r.recurrence_anchor || null;
      if (r.recurrence_pattern === 'monthly_nth_dow')  out.recurrence_nth_week = r.recurrence_nth_week || 1;
      if (r.recurrence_pattern === 'monthly_date')     out.recurrence_day_of_month = r.recurrence_day_of_month || 1;
      if (r.effective_from)  out.effective_from = r.effective_from;
      if (r.effective_to)    out.effective_to   = r.effective_to;
      if (r.recurrence_count) out.recurrence_count = r.recurrence_count;
      return out;
    });
    if (slots.length === 0) {
      toast.error('Pick at least one slot to import');
      return;
    }
    setPhase('saving');
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/venues/${venueId}/slots/import/${importId}/confirm`,
        { slots }
      );
      toast.success(`Imported ${data.inserted} slot${data.inserted === 1 ? '' : 's'}`);
      onImported();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not confirm import');
      setPhase('preview');
    }
  }

  async function reassign() {
    if (!reassignTo) {
      toast.error('Pick a target venue');
      return;
    }
    setPhase('saving');
    let confirmRes;
    try {
      // Step 1: insert into the SOURCE venue (current) so each slot
      // gets the slot_import_id link.
      const { data } = await api.post(
        `/leagues/${leagueId}/venues/${venueId}/slots/import/${importId}/confirm`,
        { slots: rows.filter(r => r.include).map(r => ({
            day_of_week: r.day_of_week,
            start_time: r.start_time,
            duration_minutes: r.duration_minutes,
            notes: r.notes || null,
            recurrence_pattern: r.recurrence_pattern || 'weekly',
            recurrence_anchor: r.recurrence_pattern === 'biweekly' ? (r.recurrence_anchor || null) : null,
            recurrence_nth_week: r.recurrence_pattern === 'monthly_nth_dow' ? (r.recurrence_nth_week || 1) : null,
            recurrence_day_of_month: r.recurrence_pattern === 'monthly_date' ? (r.recurrence_day_of_month || 1) : null,
            effective_from: r.effective_from || null,
            effective_to: r.effective_to || null,
            recurrence_count: r.recurrence_count || null,
          })) }
      );
      confirmRes = data;
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save slots');
      setPhase('preview');
      return;
    }
    try {
      // Step 2: bulk-update league_venue_id to the target.
      await api.post(
        `/leagues/${leagueId}/venues/${venueId}/slots/import/${importId}/reassign`,
        { target_venue_id: reassignTo }
      );
      toast.success(`Imported ${confirmRes.inserted} slots — moved to the target venue`);
      onImported();
    } catch (err) {
      // Step 1 succeeded but step 2 failed — slots are in the wrong
      // venue. Surface clearly so the manager knows where they
      // landed and can manually move via the regular Edit flow.
      toast.error(
        `Imported ${confirmRes.inserted} slots to ${venueName}, but the move failed: ` +
        (err?.response?.data?.error || 'unknown error') +
        '. The slots are saved — you can move them manually from the venues list.'
      );
      onImported();
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }} onClick={cancelImport}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(900px, 100%)',
        // Mobile fix: 90vh doesn't account for the on-screen keyboard /
        // Capacitor safe-area shrink, which can push the confirm button
        // off-screen. --mobile-h tracks the live viewport (see index.css).
        maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflow: 'auto',
        background: 'var(--surface)', borderRadius: 14,
        padding: 24, border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Import slots for {venueName}
          </h3>
          <button type="button" onClick={cancelImport} style={{
            background: 'transparent', border: 'none', color: 'var(--text-2)',
            cursor: 'pointer', padding: 4,
          }}>
            <X size={18} />
          </button>
        </div>

        {phase === 'pick' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 16 }}>
              Upload a PDF, image, or Excel file containing the venue's recurring schedule.
              The AI will read it and propose a list of slots. You'll review and confirm before anything is saved.
            </p>
            <div style={{
              padding: 32, borderRadius: 10,
              border: '2px dashed var(--border)',
              textAlign: 'center', marginBottom: 16,
            }}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: 'block', margin: '0 auto' }}
              />
              {file && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text)' }}>
                  Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(0)} KB)
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={cancelImport}>Cancel</button>
              <button type="button" className="btn-ice" onClick={startExtract} disabled={!file}>
                <Upload size={14} style={{ marginRight: 6 }} />
                Extract slots
              </button>
            </div>
          </>
        )}

        {phase === 'uploading' && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 14px', width: 22, height: 22 }} />
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              Extracting slots… this can take 30-60s for multi-page PDFs.
            </div>
          </div>
        )}

        {phase === 'preview' && extracted && (
          <>
            {extracted.venue_name_mismatch && (
              <div style={{
                padding: 12, marginBottom: 14,
                background: 'var(--penalty-a12)',
                border: '1px solid var(--penalty-a30)',
                borderRadius: 10,
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <AlertTriangle size={16} color="var(--penalty)" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--penalty)', marginBottom: 4 }}>
                    Possible venue mismatch
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
                    The document seems to be for{' '}
                    <strong style={{ color: 'var(--text)' }}>{extracted.venue_name_detected || 'an unknown venue'}</strong>,
                    but you're importing to <strong style={{ color: 'var(--text)' }}>{venueName}</strong>. Double-check
                    before confirming — or use the reassign option below to send these slots to a different venue.
                  </div>
                </div>
              </div>
            )}

            <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
              Found <strong style={{ color: 'var(--text)' }}>{rows.length}</strong> slot{rows.length === 1 ? '' : 's'}.
              Uncheck any you don't want to import. Edits below are applied at confirmation.
            </div>

            <div style={{ marginBottom: 14, maxHeight: '50vh', overflow: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-2)' }}>
                  <tr>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-2)' }}>Include</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-2)' }}>Day</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-2)' }}>Start</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-2)' }}>Dur</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-2)' }}>Repeats</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-2)' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid var(--border)', opacity: r.include ? 1 : 0.45 }}>
                      <td style={{ padding: '6px 10px' }}>
                        <input type="checkbox" checked={r.include}
                          onChange={(e) => setRows(rs => rs.map((rr, i) => i === idx ? { ...rr, include: e.target.checked } : rr))} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <select value={r.day_of_week}
                          onChange={(e) => setRows(rs => rs.map((rr, i) => i === idx ? { ...rr, day_of_week: parseInt(e.target.value, 10) } : rr))}
                          style={inputStyle}>
                          {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input type="time" value={r.start_time}
                          onChange={(e) => setRows(rs => rs.map((rr, i) => i === idx ? { ...rr, start_time: e.target.value } : rr))}
                          style={inputStyle} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input type="number" min={1} max={1440} value={r.duration_minutes}
                          onChange={(e) => setRows(rs => rs.map((rr, i) => i === idx ? { ...rr, duration_minutes: parseInt(e.target.value, 10) || 0 } : rr))}
                          style={{ ...inputStyle, width: 60 }} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <select value={r.recurrence_pattern || 'weekly'}
                          onChange={(e) => setRows(rs => rs.map((rr, i) => i === idx ? { ...rr, recurrence_pattern: e.target.value } : rr))}
                          style={inputStyle}>
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Biweekly</option>
                          <option value="monthly_nth_dow">Monthly (Nth)</option>
                          <option value="monthly_date">Monthly (date)</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input type="text" value={r.notes || ''}
                          onChange={(e) => setRows(rs => rs.map((rr, i) => i === idx ? { ...rr, notes: e.target.value } : rr))}
                          style={inputStyle} />
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: 'var(--text-3)', fontStyle: 'italic' }}>
                      No slots could be extracted. Try a different file or a clearer screenshot.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {otherVenues.length > 0 && (
              <div style={{
                padding: 12, marginBottom: 14,
                background: 'var(--surface-2)', borderRadius: 10,
                border: '1px solid var(--border)',
                display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                  Wrong venue?
                </span>
                <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} style={inputStyle}>
                  <option value="">Reassign all to…</option>
                  {otherVenues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <button type="button" className="btn-ghost" style={tinyBtn} disabled={!reassignTo}
                  onClick={reassign}>
                  Confirm & move
                </button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={cancelImport}>Cancel</button>
              <button type="button" className="btn-ice" onClick={confirmImport} disabled={rows.filter(r => r.include).length === 0}>
                <Save size={14} style={{ marginRight: 6 }} />
                Confirm import ({rows.filter(r => r.include).length})
              </button>
            </div>
          </>
        )}

        {phase === 'saving' && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 14px', width: 22, height: 22 }} />
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Saving…</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Blackouts section
// ───────────────────────────────────────────────────────────────

function BlackoutsSection({ leagueId, venueId, blackouts, canManage, loadingDetail, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyBlackoutForm());

  function emptyBlackoutForm() {
    return { blackout_date: '', start_time: '', end_time: '', reason: '' };
  }

  async function save() {
    if (!form.blackout_date) {
      toast.error('Pick a date');
      return;
    }
    const payload = {
      blackout_date: form.blackout_date,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      reason: form.reason.trim() || null,
    };
    try {
      await api.post(`/leagues/${leagueId}/venues/${venueId}/blackouts`, payload);
      toast.success('Blackout added');
      setForm(emptyBlackoutForm());
      setAdding(false);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add blackout');
    }
  }

  async function remove(b) {
    const ok = window.confirm(`Delete the blackout on ${b.blackout_date}?`);
    if (!ok) return;
    try {
      await api.delete(`/leagues/${leagueId}/venues/${venueId}/blackouts/${b.id}`);
      toast.success('Blackout removed');
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove blackout');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Calendar size={14} color="var(--puck)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Blackout dates
          </span>
        </div>
        {canManage && !adding && (
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => setAdding(true)}>
            <Plus size={11} style={{ marginRight: 4 }} />
            Add blackout
          </button>
        )}
      </div>

      {loadingDetail ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      ) : blackouts.length === 0 && !adding ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic', padding: '8px 0' }}>
          No blackouts. Add dates when this venue is unavailable despite a normal slot
          (holidays, tournaments, ice cut for repairs).
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {blackouts.map(b => (
            <div key={b.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 12px',
              background: 'var(--surface)', borderRadius: 8,
              border: '1px solid var(--border)',
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 8,
                background: 'var(--puck-a15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--puck)', flexShrink: 0,
              }}>
                <AlertTriangle size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {b.blackout_date}
                  {(b.start_time && b.end_time) && (
                    <span style={{ color: 'var(--text-2)', fontWeight: 400, marginLeft: 8 }}>
                      {formatTime(b.start_time)} – {formatTime(b.end_time)}
                    </span>
                  )}
                  {!b.start_time && !b.end_time && (
                    <span style={{ color: 'var(--text-2)', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>
                      (all day)
                    </span>
                  )}
                </div>
                {b.reason && (
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{b.reason}</div>
                )}
              </div>
              {canManage && (
                <button type="button" className="btn-ghost"
                  style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={() => remove(b)}>
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          ))}
          {adding && (
            <div style={{
              padding: 12, borderRadius: 8,
              background: 'var(--surface)', border: '1px solid var(--ice-a25)',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <input type="date" value={form.blackout_date}
                onChange={(e) => setForm(f => ({ ...f, blackout_date: e.target.value }))}
                style={inputStyle} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="time" value={form.start_time}
                  onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
                  placeholder="(all day)" style={{ ...inputStyle, flex: 1 }} />
                <span style={{ color: 'var(--text-2)', fontSize: 12 }}>to</span>
                <input type="time" value={form.end_time}
                  onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
                  placeholder="(all day)" style={{ ...inputStyle, flex: 1 }} />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                Leave both times empty for a whole-day blackout.
              </span>
              <input type="text" value={form.reason} maxLength={200}
                onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Reason (optional)" style={inputStyle} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn-ghost" style={tinyBtn}
                  onClick={() => { setAdding(false); setForm(emptyBlackoutForm()); }}>
                  Cancel
                </button>
                <button type="button" className="btn-ice" style={tinyBtn} onClick={save}>
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Create-venue modal
// ───────────────────────────────────────────────────────────────

function CreateVenueModal({ leagueId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/venues`, {
        name: name.trim(),
        address: address.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success('Venue created');
      onCreated(data?.venue);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not create venue');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{
        maxWidth: 480, width: '100%',
        // Mobile fix: cap height against the live viewport and scroll
        // internally so the name/address/notes fields + Save button
        // stay reachable when the on-screen keyboard is open.
        maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 className="section-title text-lg">Add venue</h2>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={fieldLabelStyle}>Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Rink A — Activa Sportsplex" maxLength={100}
            autoFocus style={inputStyle} />
        </label>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={fieldLabelStyle}>Address</span>
          <AddressAutocomplete value={address} onChange={setAddress}
            placeholder="135 Lennox Lewis Way, Kitchener…" style={inputStyle} />
        </label>

        <label style={{ display: 'block', marginBottom: 20 }}>
          <span style={fieldLabelStyle}>Notes <span style={fieldHintInlineStyle}>(optional)</span></span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Parking on north side; locker rooms on second floor…"
            rows={2} maxLength={500}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Add venue'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Small helpers
// ───────────────────────────────────────────────────────────────

function formatTime(t) {
  // Accept 'HH:MM' or 'HH:MM:SS'. Render 'HH:MM'.
  if (!t) return '';
  return t.slice(0, 5);
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const fieldLabelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 };
const fieldHintInlineStyle = { fontWeight: 400, opacity: 0.7 };
const tinyBtn = { padding: '4px 10px', fontSize: 11, minHeight: 'auto' };
const modalBackdrop = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, zIndex: 100,
};
